import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, eur, fileUrl, formatApiError } from "@/lib/api";
import OrderEditDialog from "@/components/common/OrderEditDialog";
import { Dialog as CDialog, DialogContent as CDialogContent, DialogHeader as CDialogHeader, DialogTitle as CDialogTitle, DialogDescription as CDialogDescription, DialogFooter as CDialogFooter } from "@/components/ui/dialog";
import { useSocket } from "@/lib/socket";
import { translations } from "@/lib/i18n";
import ProductModal from "@/components/client/ProductModal";
import UpsellSheet from "@/components/client/UpsellSheet";
import CancelledOverlay from "@/components/client/CancelledOverlay";
import CartDrawer from "@/components/client/CartDrawer";
import OrderTracking from "@/components/client/OrderTracking";
import { MenuCategoryBar } from "@/components/client/MenuCategoryBar";
import { DailySpecials } from "@/components/client/DailySpecialCard";
import BillView from "@/components/client/BillView";
import ReviewPrompt from "@/components/client/ReviewPrompt";
import { SessionEndedView } from "@/components/client/SessionEndedView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Plus, Bell, ShoppingBag, Star, Utensils, Wifi, WifiOff, UtensilsCrossed, ClipboardList, Euro, Flame, Heart, Sun } from "lucide-react";

let cartSeq = 0;

function getClientSessionId() {
  let id = localStorage.getItem("novanext_client_id");
  if (!id) {
    id = (crypto?.randomUUID?.() || `c_${Date.now()}_${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("novanext_client_id", id);
  }
  return id;
}

export default function ClientApp() {
  const { slug, tableNumber } = useParams();
  const [lang, setLang] = useState("FR");
  const t = translations[lang];

  const [screen, setScreen] = useState("welcome"); // welcome | menu | tracking
  const [restaurant, setRestaurant] = useState(null);
  const [menu, setMenu] = useState({ categories: [], products: [] });
  const [top, setTop] = useState({ enough: false, product_ids: [] });
  const [specials, setSpecials] = useState([]);
  const loadExtras = useCallback(() => {
    api.getTopProducts(slug).then(setTop).catch(() => {});
    api.getPublicSpecials(slug).then(setSpecials).catch(() => {});
  }, [slug]);
  useEffect(() => { loadExtras(); const id = setInterval(loadExtras, 120000); return () => clearInterval(id); }, [loadExtras]);
  const scrollMemo = useRef(0);
  const openProduct = (p) => { scrollMemo.current = window.scrollY; setSelectedProduct(p); };
  const closeProduct = () => { setSelectedProduct(null); requestAnimationFrame(() => window.scrollTo({ top: scrollMemo.current })); };
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // "closed" : la table vient d'être clôturée (fin de service) · "unavailable" : table fermée / verrouillée au scan
  const [ended, setEnded] = useState(null);
  const endSession = useCallback((kind = "closed") => {
    setTableSessionId(null); setOrders([]); writeStoredIds([]); setCart([]); setCartOpen(false); setEnded(kind);
    // eslint-disable-next-line
  }, []);
  const isSessionError = (e) => {
    const code = e?.response?.data?.detail?.code;
    if (e?.response?.status === 423 || code === "table_closed") { endSession("unavailable"); return true; }
    if (e?.response?.status === 409 && code === "session_closed") { endSession("closed"); return true; }
    return false;
  };
  const checkTableAccess = async () => {
    let s;
    try { s = await api.getTableSession(slug, tableNumber); }
    catch (e) { if (e?.response?.status === 404) { setNotFound(true); return false; } throw e; }
    if (s.status !== "OPEN" && (s.state === "LOCKED" || s.state === "STAFF_ONLY")) { endSession(readStoredIds().length ? "closed" : "unavailable"); return false; }
    return s;
  };

  const [guestName, setGuestName] = useState("");
  const [session, setSession] = useState(null);
  const [tableSessionId, setTableSessionId] = useState(null);
  const clientSessionId = useRef(getClientSessionId());

  // persist this client's order ids per table so they can return and see live progress
  const ordersKey = `novanext_orders_${slug}_${tableNumber}`;
  const readStoredIds = () => { try { return JSON.parse(localStorage.getItem(ordersKey) || "[]"); } catch { return []; } };
  const writeStoredIds = (ids) => { try { localStorage.setItem(ordersKey, JSON.stringify(ids)); } catch (_) {} };
  const didRehydrate = useRef(false);

  const [activeCat, setActiveCat] = useState("ALL");
  useEffect(() => {
    if ((activeCat === "SPECIAL" && !specials.length) || (activeCat === "TOP" && !(top.enough && top.product_ids?.length))) setActiveCat("ALL");
  }, [specials, top, activeCat]);
  useEffect(() => { if (activeCat === "SPECIAL") window.scrollTo({ top: 0, behavior: "smooth" }); }, [activeCat]);
  const [search, setSearch] = useState("");

  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [orders, setOrders] = useState([]);
  const [editOrder, setEditOrder] = useState(null);
  const [cancelOrder, setCancelOrder] = useState(null);
  const [changeOrder, setChangeOrder] = useState(null);
  const additionParent = useRef(null);
  const startAddition = (o) => { additionParent.current = o.id; setScreen("menu"); toast(t.additionHint); };
  const submitChangeRequest = async (items) => {
    const o = changeOrder; const r = await api.clientChangeRequest(o.id, items, clientSessionId.current, `${o.id}-${Date.now()}`);
    setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, pending_change_request_id: r.id } : x)));
    toast(t.changeRequestSent); return { ...o, pending_change_request_id: r.id };
  };
  const [cancelledInfo, setCancelledInfo] = useState(null);
  const showCancelled = (byClient) => setCancelledInfo({ title: byClient ? t.cancelledTitle : t.cancelledByRestoTitle, text: byClient ? t.cancelledByClientText : t.refusedText, hint: t.cancelledHint });
  const closeCancelled = useCallback(() => setCancelledInfo(null), []);
  const applyOrderUpdate = (o) => setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, ...o } : x)));
  const doClientCancel = async () => {
    const o = cancelOrder; setCancelOrder(null);
    try { const r = await api.clientCancelOrder(o.id, clientSessionId.current); applyOrderUpdate(r); showCancelled(true); }
    catch (e) { if (isSessionError(e)) return; const d = formatApiError(e.response?.data?.detail); toast(e.response?.status === 409 && d ? d : t.cancelledByClientText); }
  };
  const [callDialog, setCallDialog] = useState(false);
  const [waiterActive, setWaiterActive] = useState(false);
  const ordersRef = useRef([]);

  // ---- bill / payment ----
  const [bill, setBill] = useState(null);
  const [billLoading, setBillLoading] = useState(false);
  const [payMode, setPayMode] = useState(null); // legacy, unused
  const [paying, setPaying] = useState(false);
  const screenRef = useRef("welcome");

  // ---- realtime ----
  const rooms = useMemo(() => {
    const r = [`table:${slug}:${tableNumber}`, `menu:${slug}`];
    orders.forEach((o) => o?.id && r.push(`order:${o.id}`));
    return r;
  }, [slug, tableNumber, orders]);

  const { connected } = useSocket(rooms, {
    order_updated: (data) => {
      const mine = ordersRef.current.find((o) => o.id === data.id);
      if (!mine) return;
      if (mine.status !== data.status) {
        if (data.status === "ACCEPTEE") toast.success(t.acceptedText);
        if (data.status === "EN_PREPARATION") toast.success(t.inPrepText);
        if (data.status === "PRETE") toast.success(t.readyText);
        if (data.status === "SERVIE") toast.success(t.servedText);
        if (data.status === "REFUSEE") showCancelled(data.refused_by === "client");
      }
      setOrders((prev) => prev.map((o) => (o.id === data.id ? { ...o, ...data } : o)));
    },
    service_request_updated: (data) => {
      if (data.handled) {
        setWaiterActive(false);
        toast.success(t.waiterComing);
      }
    },
    menu_updated: () => {
      api.getMenu(slug, true).then(setMenu).catch(() => {});
      loadExtras();
    },
    payment_updated: () => {
      if (screenRef.current === "bill") refreshBill();
    },
    change_request_decided: (cr) => {
      if (!ordersRef.current.find((o) => o.id === cr.order_id)) return;
      setOrders((prev) => prev.map((x) => (x.id === cr.order_id ? { ...x, pending_change_request_id: null } : x)));
      const tid = { id: `crd-${cr.id}` }; // même événement reçu via les rooms order: et table: → dédoublonnage
      if (cr.status === "accepted") toast.success(t.changeAccepted, tid); else if (cr.status === "refused") toast(t.changeRefused, tid); else toast(t.changeExpired, tid);
    },
    session_closed: () => endSession("closed"),
    session_opened: () => { if (ended === "unavailable") { setEnded(null); setScreen("welcome"); } },
  });

  useEffect(() => {
    let mounted = true;
    Promise.all([api.getRestaurant(slug), api.getMenu(slug, true)])
      .then(([r, m]) => {
        if (!mounted) return;
        setRestaurant(r);
        setMenu(m);
        setLoading(false);
      })
      .catch(() => { setLoading(false); setNotFound(true); });
    return () => { mounted = false; };
  }, [slug]);

  // Rehydrate this client's ongoing orders on return/refresh (scoped to the current open table session)
  useEffect(() => {
    if (didRehydrate.current) return;
    didRehydrate.current = true;
    const ids = readStoredIds();
    (async () => {
      let sessionId = null;
      try {
        const s = await checkTableAccess();
        if (s === false) return;
        if (s && s.status === "OPEN") sessionId = s.table_session_id;
      } catch (_) {}
      if (!ids.length) return;
      if (!sessionId) { writeStoredIds([]); return; }
      const fetched = await Promise.all(ids.map((id) => api.getOrder(id, clientSessionId.current).catch(() => null)));
      const valid = fetched.filter((o) => o && o.table_session_id === sessionId);
      if (!valid.length) { writeStoredIds([]); return; }
      writeStoredIds(valid.map((o) => o.id));
      setOrders(valid);
      setTableSessionId(sessionId);
      setScreen("tracking");
    })();
    // eslint-disable-next-line
  }, [slug, tableNumber]);

  // check existing waiter request
  useEffect(() => {
    api.getTableServiceRequest(slug, tableNumber).then((r) => {
      if (r && r.id && !r.handled) setWaiterActive(true);
    }).catch(() => {});
  }, [slug, tableNumber]);

  const enterMenu = async () => {
    try {
      const res = await api.joinSession({
        restaurant_slug: slug, table_number: tableNumber,
        client_session_id: clientSessionId.current, guest_name: guestName || null,
      });
      setTableSessionId(res.table_session_id);
    } catch (e) { if (isSessionError(e)) return; }
    setScreen("menu");
  };

  // ---- cart ----
  const addToCart = (item) => {
    setCart((prev) => [...prev, { ...item, cart_id: ++cartSeq }]);
    setSelectedProduct(null);
    toast.success(t.addedToCart(item.name));
    // upsell for mains/burgers if enabled
    if (restaurant?.upsell_enabled && ["Burgers", "Plats", "Pâtes"].includes(item.category)) {
      setTimeout(() => setUpsellOpen(true), 250);
    }
  };

  const upsellSuggestions = useMemo(() => {
    return menu.products
      .filter((p) => ["Boissons", "Desserts"].includes(p.category))
      .slice(0, 4);
  }, [menu]);

  const [upsellBatch, setUpsellBatch] = useState([]); // cart_id des upsells ajoutés depuis le pop-up courant
  const addUpsell = (p) => {
    const existing = cart.find((i) => upsellBatch.includes(i.cart_id) && i.product_id === p.id);
    if (existing) { changeQty(existing.cart_id, 1); return; }
    const cid = ++cartSeq;
    setUpsellBatch((b) => [...b, cid]);
    setCart((prev) => [...prev, {
      cart_id: cid, product_id: p.id, name: p.name, image: p.image,
      base_price: p.price, quantity: 1, cooking: null, side: null, supplements: [],
      instructions: null, unit_price: p.price, line_total: p.price, category: p.category, from_upsell: true,
    }]);
    api.trackReco({
      restaurant_slug: slug, table_number: tableNumber, table_session_id: tableSessionId,
      client_session_id: clientSessionId.current, event: "added", product_id: p.id, product_name: p.name, price: p.price,
    }).catch(() => {});
    toast.success(t.addedToCart(p.name));
  };

  useEffect(() => {
    if (upsellOpen) setUpsellBatch([]);
    if (upsellOpen && upsellSuggestions.length) {
      upsellSuggestions.forEach((p) => api.trackReco({
        restaurant_slug: slug, table_number: tableNumber, table_session_id: tableSessionId,
        client_session_id: clientSessionId.current, event: "shown", product_id: p.id, product_name: p.name, price: p.price,
      }).catch(() => {}));
    }
    // eslint-disable-next-line
  }, [upsellOpen]);

  useEffect(() => {
    document.title = restaurant ? `NOVANEXT TABLE · ${restaurant.name}` : "NOVANEXT TABLE";
  }, [restaurant]);

  const changeQty = (cartId, delta) => {
    setCart((prev) => prev.map((i) => {
      if (i.cart_id !== cartId) return i;
      const q = Math.max(1, i.quantity + delta);
      return { ...i, quantity: q, line_total: i.unit_price * q };
    }));
  };

  const removeItem = (cartId) => setCart((prev) => prev.filter((i) => i.cart_id !== cartId));

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartTotal = cart.reduce((s, i) => s + i.line_total, 0);

  const checkout = async () => {
    if (cart.length === 0) return;
    if (submitting) return;
    const reqId = (crypto.randomUUID && crypto.randomUUID()) || `${clientSessionId.current}-${Date.now()}`;
    setSubmitting(true);
    try {
      // Vérification serveur de la session avant envoi (jamais d'acceptation locale si la validité est inconnue)
      let live;
      try { live = await api.getTableSession(slug, tableNumber); }
      catch (_) { toast.error(t.offlineNoOrder || "Connexion indisponible : commande non envoyée. Réessayez."); return; }
      if (live.status !== "OPEN" || (tableSessionId && live.table_session_id !== tableSessionId)) { endSession("closed"); return; }
      const payload = {
        restaurant_slug: slug,
        table_number: tableNumber,
        session_id: session?.id || null,
        table_session_id: tableSessionId || live.table_session_id,
        client_session_id: clientSessionId.current,
        client_request_id: reqId,
        guest_name: guestName || null,
        parent_order_id: additionParent.current || null,
        subtotal: cartTotal,
        items: cart.map((i) => ({
          product_id: i.product_id, name: i.name, base_price: i.base_price,
          quantity: i.quantity, cooking: i.cooking, side: i.side,
          supplements: i.supplements, options: i.options || [], formula: i.formula || [],
          instructions: i.instructions, line_total: i.line_total,
        })),
      };
      const created = await api.createOrder(payload);
      if (additionParent.current) { additionParent.current = null; toast.success(t.additionSent); }
      cart.filter((i) => i.from_upsell).forEach((i) => api.trackReco({
        restaurant_slug: slug, table_number: tableNumber, table_session_id: tableSessionId,
        client_session_id: clientSessionId.current, event: "purchased",
        product_id: i.product_id, product_name: i.name, price: i.line_total, order_id: created.id,
      }).catch(() => {}));
      setOrders((prev) => [created, ...prev.filter((o) => o.id !== created.id)]);
      writeStoredIds([created.id, ...readStoredIds().filter((x) => x !== created.id)]);
      setCart([]);
      setCartOpen(false);
      setScreen("tracking");
      toast.success(t.sentText);
    } catch (e) {
      if (!isSessionError(e)) toast.error(formatApiError(e?.response?.data?.detail) || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  const callWaiter = async (type = "SERVEUR", label = null) => {
    setCallDialog(false);
    try {
      const res = await api.createServiceRequest({
        restaurant_slug: slug, table_number: tableNumber, session_id: session?.id || null,
        table_session_id: tableSessionId || null, client_session_id: clientSessionId.current,
        type, label: label || undefined,
      });
      setWaiterActive(true);
      if (res.already_active) toast.info(t.requestAlready);
      else toast.success(t.srvSent);
    } catch (e) { if (!isSessionError(e)) toast.error("Erreur"); }
  };

  // ---- bill / payment ----
  // « Paiement effectué » : une seule fois par passage non payé → payé (jamais rejoué par le polling
  // 5 s ni par payment_updated ; une nouvelle addition d'une autre session re-notifie naturellement).
  const paidNotifiedRef = useRef(null);
  const refreshBill = async () => {
    try {
      const b = await api.getPublicBill(slug, tableNumber);
      setBill(b);
      const key = b.table_session_id || `${slug}:${tableNumber}`;
      if (b.fully_paid && paidNotifiedRef.current !== key) {
        paidNotifiedRef.current = key;
        toast.success(t.paidTitle, { id: `paid-${key}` });
      } else if (!b.fully_paid && paidNotifiedRef.current === key) {
        paidNotifiedRef.current = null; // nouvelle consommation sur la même table → prochain paiement notifié
      }
    } catch (e) { /* silent */ }
  };

  const openBill = async () => {
    setScreen("bill");
    additionParent.current = null;
    setBillLoading(true);
    try {
      const b = await api.getPublicBill(slug, tableNumber);
      setBill(b);
    } catch (e) { toast.error("Erreur lors du chargement de l'addition"); }
    finally { setBillLoading(false); }
  };

  const handlePay = async (method, selection) => {
    setPaying(true);
    try {
      if (method === "stripe") {
        localStorage.setItem("novanext_pay_return", JSON.stringify({ slug, tableNumber }));
        const res = await api.createCheckout({
          restaurant_slug: slug, table_number: tableNumber, origin_url: window.location.origin,
          split_mode: selection.split_mode, amount: selection.amount, guests: selection.guests, shares: selection.shares,
          tip: selection.tip, email: selection.email,
        });
        window.location.href = res.checkout_url;
        return;
      }
      // cash
      await api.cashRequest({
        restaurant_slug: slug, table_number: tableNumber,
        split_mode: selection.split_mode, amount: selection.amount, guests: selection.guests, shares: selection.shares,
        tip: selection.tip, email: selection.email,
      });
      toast.success(t.cashRequested);
      await refreshBill();
    } catch (e) {
      if (!isSessionError(e)) toast.error(formatApiError(e?.response?.data?.detail) || "Erreur de paiement");
    } finally { setPaying(false); }
  };

  // refresh bill periodically while viewing it (reflects others' payments)
  useEffect(() => {
    screenRef.current = screen;
    if (screen !== "bill") return;
    const id = setInterval(refreshBill, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [screen, slug, tableNumber]);

  // keep a live ref of orders for polling closures
  useEffect(() => { ordersRef.current = orders; }, [orders]);

  // reliable order-status sync: Socket.IO (realtime) + polling fallback (reads the true DB status)
  useEffect(() => {
    if (screen !== "tracking") return;
    const poll = async () => {
      const active = ordersRef.current.filter((o) => !["SERVIE", "REFUSEE"].includes(o.status));
      if (!active.length) return;
      const results = await Promise.all(active.map((o) => api.getOrder(o.id, clientSessionId.current).catch(() => null)));
      setOrders((prev) => prev.map((o) => {
        const upd = results.find((r) => r && r.id === o.id);
        return upd && upd.status !== o.status ? { ...o, ...upd } : o;
      }));
    };
    poll();
    const id = setInterval(poll, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [screen]);

  const filteredProducts = useMemo(() => {
    let list = menu.products;
    if (activeCat === "TOP") {
      const byId = Object.fromEntries(list.map((p) => [p.id, p]));
      list = (top.product_ids || []).map((id) => byId[id]).filter(Boolean);
    } else if (activeCat === "SPECIAL") list = [];
    else if (activeCat !== "ALL") list = list.filter((p) => p.category === activeCat);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q));
    }
    return list;
  }, [menu, activeCat, search, top]);

  const CAT_EMOJI = { "Formules": "📋", "Entrées": "🍲", "Salades": "🥗", "Plats": "🍖", "Burgers": "🍔", "Pâtes": "🍝", "Accompagnements": "🍟", "Sauces": "🥣", "Fromages": "🧀", "Desserts": "🍰", "Boissons": "🥤", "Cocktails": "🍸", "Menu Enfant": "🧸" };
  const showHeaders = activeCat === "ALL";
  const groups = showHeaders
    ? menu.categories.map((c) => ({ name: c.name, items: filteredProducts.filter((p) => p.category === c.name) })).filter((g) => g.items.length)
    : [{ name: activeCat === "TOP" ? t.topOrdered : activeCat, items: filteredProducts }];
  const addSpecial = (s) => addToCart({
    product_id: `special:${s.id}`, name: s.name, image: s.image, base_price: s.price, quantity: 1, options: [], formula: [],
    cooking: null, side: null, supplements: [], instructions: null, unit_price: s.price, line_total: s.price, category: "Plat du jour",
  });

  if (loading) {
    return <div className="min-h-screen bg-[#FBF9F5] grid place-items-center"><div className="w-8 h-8 rounded-full border-2 border-[#123026] border-t-transparent animate-spin" /></div>;
  }
  if (notFound || !restaurant) {
    return (
      <div data-testid="restaurant-not-found" className="min-h-screen bg-[#FBF9F5] grid place-items-center px-6 text-center">
        <div>
          <div className="w-16 h-16 rounded-full bg-[#FAF3E0] border border-[#C69C4A]/40 grid place-items-center mx-auto mb-4"><Utensils className="w-7 h-7 text-[#C69C4A]" /></div>
          <h1 className="font-serif-display text-3xl text-[#123026] font-semibold">{t.restaurantNotFound}</h1>
          <p className="text-sm text-[#8A918D] mt-2">{t.restaurantNotFoundHint}</p>
        </div>
      </div>
    );
  }

  const primary = restaurant?.brand_primary || "#123026";
  const gold = restaurant?.brand_secondary || "#C69C4A";
  const theme = { primary, gold };
  const Logo = ({ className }) => (
    restaurant?.logo_image
      ? <img src={fileUrl(restaurant.logo_image)} alt="logo" className={`${className} object-cover`} />
      : <span className="font-serif-display" style={{ color: gold }}>{restaurant.logo_letter}</span>
  );

  const LangSwitch = () => (
    <div className="flex items-center gap-1 text-xs font-semibold">
      {["FR", "EN"].map((l) => (
        <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)} className={`px-2 py-1 rounded-md ${lang === l ? "bg-[#123026] text-white" : "text-[#8A918D]"}`}>{l}</button>
      ))}
    </div>
  );

  if (ended) {
    return <SessionEndedView restaurant={restaurant} kind={ended} Logo={Logo} lang={lang} onRetry={() => checkTableAccess().then((s) => { if (s) { setEnded(null); setScreen("welcome"); } }).catch(() => {})} />;
  }

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <div className="w-full min-h-screen bg-[#FBF9F5] relative">
        {/* ---------- WELCOME ---------- */}
        {screen === "welcome" && (
          <div className="min-h-screen flex flex-col animate-slide-up">
            <div className="relative h-[44vh] min-h-[320px] w-full overflow-hidden">
              <img src={restaurant.hero_image} alt={restaurant.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-[#FBF9F5]" />
              <div className="absolute top-4 right-4"><LangSwitch /></div>
            </div>
            <div className="flex-1 px-4 -mt-24 relative flex flex-col max-w-md w-full mx-auto pb-8">
              <div className="bg-white/95 backdrop-blur-xl rounded-3xl px-6 pt-0 pb-7 sm:px-8 border border-[#EAE5DC] shadow-2xl text-center">
                <div className="w-20 h-20 rounded-2xl grid place-items-center mx-auto -mt-10 mb-4 shadow-xl overflow-hidden ring-4 ring-white" style={{ backgroundColor: primary }}>
                  <Logo className="w-full h-full text-3xl grid place-items-center" />
                </div>
                <h1 className="font-serif-display text-3xl sm:text-4xl text-[#123026] leading-tight font-bold tracking-tight">{restaurant.name}</h1>
                <p className="text-sm text-[#8A918D] mt-1.5">{restaurant.tagline}</p>

                <div data-testid="table-badge" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#FAF3E0] border border-[#C69C4A]/40 px-4 py-2 text-[#123026] text-xs font-bold uppercase tracking-wider shadow-sm">
                  <Utensils className="w-4 h-4 text-[#C69C4A]" /> {t.welcome} · {t.table} {tableNumber}
                </div>

                <p className="text-[#5C615E] text-sm mt-4 leading-relaxed">{t.intro}</p>

                <div className="mt-6 text-left">
                  <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A918D]">{t.firstName}</label>
                  <Input
                    data-testid="guest-name-input"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder={t.firstNamePlaceholder}
                    className="mt-1.5 bg-white border-[#EAE5DC] rounded-xl h-12 focus:border-[#C69C4A] focus:ring-1 focus:ring-[#C69C4A]"
                  />
                </div>

                <div className="mt-6 space-y-3">
                  <Button data-testid="btn-view-menu" onClick={enterMenu} className="w-full h-14 rounded-full text-white font-bold text-base shadow-xl hover:brightness-110 active:scale-[0.98] transition-all" style={{ backgroundColor: primary }}>
                    {t.viewMenu}
                  </Button>
                  <Button data-testid="btn-call-waiter-welcome" onClick={() => setCallDialog(true)} variant="outline" className="w-full h-12 rounded-full border-[#C69C4A] text-[#123026] hover:bg-[#FAF3E0] font-semibold transition-all">
                    <Bell className="w-4 h-4 mr-2 text-[#C69C4A]" /> {t.callWaiter}
                  </Button>
                </div>
              </div>
              <p className="text-center text-[11px] text-[#B4B9B4] pt-5">{t.poweredBy}</p>
            </div>
          </div>
        )}

        {/* ---------- MENU ---------- */}
        {screen === "menu" && (
          <div className="pb-36">
            <header className="sticky top-0 z-40 bg-[#FBF9F5]/95 backdrop-blur-md border-b border-[#EAE5DC]">
              <div className="px-4 pt-3 pb-2 flex items-center justify-between max-w-6xl mx-auto">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg grid place-items-center overflow-hidden" style={{ backgroundColor: primary }}>
                    <Logo className="w-full h-full text-lg grid place-items-center" />
                  </div>
                  <div>
                    <p className="font-serif-display text-base text-[#123026] leading-none">{restaurant.name}</p>
                    <p className="text-[11px] text-[#8A918D] mt-0.5 flex items-center gap-1">
                      {connected ? <Wifi className="w-3 h-3 text-emerald-600" /> : <WifiOff className="w-3 h-3 text-[#B4B9B4]" />}
                      {t.table} {tableNumber}
                    </p>
                  </div>
                </div>
                <LangSwitch />
              </div>
              <div className="px-4 pb-3 max-w-6xl mx-auto">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8A918D]" />
                  <Input
                    data-testid="menu-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t.search}
                    className="pl-10 bg-white border-[#EAE5DC] rounded-full h-11"
                  />
                </div>
              </div>
              <MenuCategoryBar activeKey={activeCat}>
                {specials.length > 0 && (
                  <CatPill active={activeCat === "SPECIAL"} onClick={() => setActiveCat("SPECIAL")} label={t.dailySpecialTag} testid="category-SPECIAL" icon={<Sun className="w-4 h-4 text-[#C69C4A] fill-[#C69C4A]/30" />} />
                )}
                {top.enough && top.product_ids?.length > 0 && (
                  <CatPill active={activeCat === "TOP"} onClick={() => setActiveCat("TOP")} label={t.topOrdered} testid="category-TOP" icon={<Flame className="w-4 h-4 text-[#E2572B] fill-[#E2572B]/30" />} />
                )}
                <CatPill active={activeCat === "ALL"} onClick={() => setActiveCat("ALL")} label={t.all} testid="category-ALL" all />
                {menu.categories.map((c) => (
                  <CatPill key={c.id} active={activeCat === c.name} onClick={() => setActiveCat(c.name)} label={c.name} testid={`category-${c.name}`} emoji={CAT_EMOJI[c.name]} />
                ))}
              </MenuCategoryBar>
            </header>

            <div className="px-4 pt-4 space-y-6 max-w-2xl mx-auto">
              {(activeCat === "ALL" || activeCat === "SPECIAL") && (!search.trim() || specials.some((s) => (s.name || "").toLowerCase().includes(search.toLowerCase()))) && <DailySpecials specials={search.trim() ? specials.filter((s) => (s.name || "").toLowerCase().includes(search.toLowerCase())) : specials} t={t} theme={theme} onAdd={addSpecial} hero={activeCat === "SPECIAL"} onBrowse={() => setActiveCat("ALL")} />}
              {activeCat === "TOP" && !filteredProducts.length && (
                <p data-testid="top-empty" className="text-sm text-[#8A918D] text-center py-10">{t.topEmpty}</p>
              )}
              {groups.map((g) => (
                <div key={g.name}>
                  {showHeaders && (
                    <div className="flex items-end gap-3 mb-4">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.22em] text-[#C69C4A] font-bold mb-1">Au menu</p>
                        <h2 className="font-serif-display text-2xl sm:text-3xl text-[#123026] font-semibold leading-none">{g.name}</h2>
                      </div>
                      <span className="text-[11px] font-bold text-[#123026] rounded-full px-2.5 py-0.5 bg-[#FAF3E0] border border-[#C69C4A]/40 mb-0.5">{g.items.length}</span>
                      <div className="flex-1 h-px bg-gradient-to-r from-[#C69C4A]/60 via-[#C69C4A]/20 to-transparent mb-2" />
                    </div>
                  )}
                  <div className="space-y-3">
                    {g.items.map((p) => {
                      const off = !p.available;
                      const offLabel = p.unavailable_label === "success" ? t.unavailableSuccess : t.unavailableNow;
                      const isSimple = (p.product_type || (p.option_groups?.length || p.formula_courses?.length ? "configurable" : "simple")) === "simple" && !(p.formula_courses?.length);
                      const onPlus = (e) => {
                        e.stopPropagation();
                        if (off) return;
                        if (isSimple) {
                          addToCart({
                            product_id: p.id, name: p.name, image: p.image, base_price: p.price,
                            quantity: 1, options: [], formula: [], cooking: null, side: null,
                            supplements: [], instructions: null, unit_price: p.price, line_total: p.price, category: p.category,
                          });
                        } else {
                          setSelectedProduct(p);
                        }
                      };
                      const rank = activeCat === "TOP" ? top.product_ids.indexOf(p.id) + 1 : 0;
                      return (
                      <div
                        key={p.id}
                        data-testid={`product-card-${p.id}`}
                        onClick={() => !off && openProduct(p)}
                        className={`group relative flex gap-4 p-3.5 sm:p-4 bg-white rounded-3xl border border-[#EAE5DC] shadow-[0_4px_16px_rgba(18,48,38,0.03)] transition-all duration-300 ${off ? "opacity-70 cursor-not-allowed" : "hover:border-[#C69C4A]/50 hover:shadow-[0_8px_24px_rgba(198,156,74,0.12)] active:scale-[0.99] cursor-pointer"}`}
                      >
                        <div className="relative w-28 h-28 sm:w-32 sm:h-32 shrink-0 rounded-2xl overflow-hidden bg-[#EFEADF] border border-[#EAE5DC]/80">
                          {p.image ? (
                            <img src={p.image} alt={p.name} loading="lazy" className={`w-full h-full object-cover transition-transform duration-500 ${off ? "grayscale" : "group-hover:scale-105"}`} />
                          ) : (
                            <div className="w-full h-full grid place-items-center">
                              <span className="font-serif-display text-3xl text-[#B69A6B]">{(p.name || "?").trim().charAt(0).toUpperCase()}</span>
                            </div>
                          )}
                          {rank > 0 && (
                            <span data-testid={`top-rank-${p.id}`} className="absolute bottom-2 right-2 w-6 h-6 rounded-full bg-[#E2572B] text-white text-[11px] font-bold grid place-items-center shadow-md">{rank}</span>
                          )}
                          {p.is_popular && !off && (
                            <span data-testid={`popular-badge-${p.id}`} className="absolute top-2 left-2 inline-flex items-center gap-1 bg-gradient-to-r from-[#C69C4A] to-[#B38B3A] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md">
                              <Heart className="w-2.5 h-2.5 fill-white" /> {t.popular}
                            </span>
                          )}
                          {off && (
                            <span className="absolute inset-0 bg-[#0A1C16]/65 backdrop-blur-[1px] grid place-items-center text-center px-1.5">
                              <span data-testid={`unavailable-badge-${p.id}`} className="text-white text-[11px] font-bold leading-tight">{offLabel}</span>
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col py-0.5">
                          <p className="font-serif-display text-lg sm:text-xl text-[#123026] leading-snug font-semibold">{p.name}</p>
                          <p className="text-xs sm:text-sm text-[#5C615E] mt-1 line-clamp-2 leading-relaxed">{p.description}</p>
                          <div className="flex items-center justify-between mt-auto pt-2.5">
                            <span className="font-serif-display text-base sm:text-lg font-bold text-[#123026] tracking-tight">{eur(p.price)}</span>
                            {off ? (
                              <span data-testid={`unavailable-text-${p.id}`} className="text-[11px] font-semibold text-[#8A918D]">{t.unavailableNow}</span>
                            ) : (
                              <button data-testid={`product-add-${p.id}`} onClick={onPlus} style={{ backgroundColor: primary, color: gold }} className="w-10 h-10 rounded-full grid place-items-center hover:brightness-110 active:scale-90 transition-all duration-200 shadow-md">
                                <Plus className="w-4 h-4" strokeWidth={2.5} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {filteredProducts.length === 0 && activeCat !== "SPECIAL" && !(search.trim() && specials.some((s) => (s.name || "").toLowerCase().includes(search.toLowerCase()))) && (
                <p className="text-center text-[#8A918D] py-12 text-sm">Aucun plat trouvé.</p>
              )}
              <p className="text-center text-[11px] text-[#B4B9B4] pt-6 pb-2">{t.poweredBy}</p>
            </div>

            {/* floating order bar (above bottom nav) */}
            {cartCount > 0 && (
              <div className="fixed z-40 inset-x-0 mx-auto w-full max-w-md md:max-w-2xl px-4 animate-slide-up" style={{ bottom: "4.9rem" }}>
                <Button
                  data-testid="floating-cart-btn"
                  onClick={() => setCartOpen(true)}
                  className="w-full h-14 py-3.5 rounded-full text-[#0A1C16] font-bold shadow-2xl flex items-center justify-between px-6 bg-gradient-to-r from-[#C69C4A] to-[#D4AF37] hover:brightness-105 active:scale-[0.98] transition-all"
                >
                  <span className="flex items-center gap-2"><ShoppingBag className="w-5 h-5" /> {cartCount} · {t.order}</span>
                  <span className="font-serif-display text-lg">{eur(cartTotal)}</span>
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ---------- TRACKING ---------- */}
        {screen === "tracking" && orders.length > 0 && (
          <div className="pb-28 max-w-md mx-auto">
            <div className="px-5 pt-6 pb-3">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#C69C4A] font-bold mb-1">{t.navOrder}</p>
              <h1 className="font-serif-display text-3xl text-[#123026] font-bold">
                {orders.length > 1 ? `${orders.length} ${t.ordersCount}` : t.orderSent}
              </h1>
            </div>
            <div className="px-4 space-y-5">
              {orders.map((o) => (
                <div key={o.id} className="space-y-2">
                  <OrderTracking order={o} t={t} theme={theme} />
                  {o.pending_change_request_id && <p data-testid={`client-change-pending-${o.id}`} className="mx-1 rounded-xl bg-[#FFF6E5] border border-[#F3DDB0] px-3 py-2 text-xs text-[#8A5A00] font-semibold text-center">{t.changePending}</p>}
                  {o.status === "NOUVELLE" && (
                    <div className="flex gap-2 px-1">
                      <Button data-testid={`client-edit-order-${o.id}`} onClick={() => setEditOrder(o)} variant="outline" className="flex-1 h-10 rounded-full border-[#DDE5E0] text-[#123026] bg-white font-semibold text-sm">{t.edit}</Button>
                      <Button data-testid={`client-cancel-order-${o.id}`} onClick={() => setCancelOrder(o)} variant="outline" className="flex-1 h-10 rounded-full border-[#F0C9C0] text-[#B4462F] bg-white font-semibold text-sm">{t.cancel}</Button>
                    </div>
                  )}
                  {["ACCEPTEE", "EN_PREPARATION", "PRETE", "SERVIE"].includes(o.status) && !o.paid && (
                    <div className="flex gap-2 px-1">
                      <Button data-testid={`client-add-to-order-${o.id}`} onClick={() => startAddition(o)} className="flex-1 h-10 rounded-full text-white font-semibold text-sm" style={{ backgroundColor: theme.primary }}>{t.addToOrder}</Button>
                      {o.status !== "SERVIE" && !o.pending_change_request_id && <Button data-testid={`client-request-change-${o.id}`} onClick={() => setChangeOrder(o)} variant="outline" className="flex-1 h-10 rounded-full border-[#DDE5E0] text-[#123026] bg-white font-semibold text-sm">{t.edit}</Button>}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="px-5 space-y-3 mt-6">
              <Button data-testid="tracking-call-waiter" onClick={() => setCallDialog(true)} variant="outline" className="w-full h-12 rounded-full border-[#123026] text-[#123026] hover:bg-[#EBF2EE] font-semibold">
                <Bell className="w-4 h-4 mr-2" /> {t.callWaiter}
              </Button>
              <Button data-testid="tracking-new-order" onClick={() => setScreen("menu")} className="w-full h-12 rounded-full text-[#0A1C16] font-bold bg-gradient-to-r from-[#C69C4A] to-[#D4AF37] hover:brightness-105 active:scale-[0.98] transition-all">
                {t.addMore}
              </Button>
              {restaurant?.reviews_enabled !== false && (
                <button data-testid="tracking-give-review" onClick={() => setScreen("review")} className="w-full text-center text-sm font-semibold text-[#8A918D] hover:text-[#123026] underline underline-offset-4 pt-1 transition-colors">
                  {t.giveReview}
                </button>
              )}
            </div>
            <p className="text-center text-[11px] text-[#B4B9B4] pt-6 pb-2">{t.poweredBy}</p>
          </div>
        )}

        {/* ---------- REVIEW ---------- */}
        {screen === "review" && (
          <div className="pb-28 max-w-md mx-auto px-5 pt-8">
            <ReviewPrompt
              slug={slug}
              tableSessionId={tableSessionId}
              clientSessionId={clientSessionId.current}
              googleUrl={restaurant?.google_review_url}
              theme={theme}
              t={t}
            />
            <p className="text-center text-[11px] text-[#B4B9B4] pt-6">{t.poweredBy}</p>
          </div>
        )}

        {/* ---------- BILL / PAYMENT ---------- */}
        {screen === "bill" && (
          <div className="pb-28 max-w-md mx-auto">
            <BillView
              clientSessionId={clientSessionId.current}
              bill={bill}
              loading={billLoading}
              t={t}
              theme={theme}
              submitting={paying}
              onPay={handlePay}
              restaurant={restaurant}
              tableSessionId={tableSessionId}
            />
          </div>
        )}

        {/* ---------- BOTTOM NAV ---------- */}
        {screen !== "welcome" && (
          <nav data-testid="bottom-nav" className="fixed z-50 bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md md:max-w-2xl bg-white/95 backdrop-blur-lg border-t border-[#EAE5DC] px-2 py-2 flex items-center justify-around shadow-[0_-4px_24px_rgba(18,48,38,0.06)]">
            <NavBtn testid="nav-menu" icon={UtensilsCrossed} label={t.navMenu} active={screen === "menu"} onClick={() => setScreen("menu")} />
            <NavBtn testid="nav-cart" icon={ShoppingBag} label={t.navCart} badge={cartCount} onClick={() => setCartOpen(true)} />
            <NavBtn testid="nav-waiter" icon={Bell} label={t.navWaiter} active={waiterActive} onClick={() => setCallDialog(true)} />
            <NavBtn testid="nav-bill" icon={Euro} label={t.navBill} active={screen === "bill"} onClick={openBill} />
            <NavBtn testid="nav-order" icon={ClipboardList} label={t.navOrder} badge={orders.length > 1 ? orders.length : 0} active={screen === "tracking"} onClick={() => { additionParent.current = null; return orders.length ? setScreen("tracking") : toast.info(t.noActiveOrder); }} />
          </nav>
        )}
      </div>

      <ProductModal
        product={selectedProduct}
        open={!!selectedProduct}
        onClose={closeProduct}
        onAdd={addToCart}
        t={t}
        theme={theme}
      />
      <CancelledOverlay info={cancelledInfo} onDone={closeCancelled} />
      <OrderEditDialog light open={!!changeOrder} order={changeOrder} slug={slug} onClose={() => setChangeOrder(null)} onSaved={() => {}} save={submitChangeRequest} notice={t.changeNotice} reduceOnly />
      <OrderEditDialog light open={!!editOrder} order={editOrder} slug={slug} onClose={() => setEditOrder(null)} onSaved={applyOrderUpdate} save={(items) => api.clientUpdateOrderItems(editOrder.id, items, clientSessionId.current)} />
      <CDialog open={!!cancelOrder} onOpenChange={(v) => !v && setCancelOrder(null)}>
        <CDialogContent data-testid="client-cancel-dialog" className="rounded-3xl bg-[#FBF9F5] border-none text-[#123026] max-w-sm w-[calc(100%-1.5rem)]">
          <CDialogHeader><CDialogTitle className="font-serif-display text-2xl">Êtes-vous sûr ?</CDialogTitle><CDialogDescription className="text-[#8A918D]">Annuler votre commande {cancelOrder?.order_code || cancelOrder?.order_number} ?</CDialogDescription></CDialogHeader>
          <CDialogFooter className="gap-2">
            <Button data-testid="client-cancel-no" variant="outline" onClick={() => setCancelOrder(null)} className="rounded-full border-[#DDE5E0] bg-white text-[#123026]">Non, garder</Button>
            <Button data-testid="client-cancel-yes" onClick={doClientCancel} className="rounded-full bg-[#B4462F] hover:bg-[#9A3A26] text-white font-bold">Oui, annuler</Button>
          </CDialogFooter>
        </CDialogContent>
      </CDialog>
      <UpsellSheet
        open={upsellOpen}
        suggestions={upsellSuggestions}
        selected={cart.filter((i) => upsellBatch.includes(i.cart_id))}
        onChangeQty={changeQty}
        onRemove={removeItem}
        onClose={() => setUpsellOpen(false)}
        onAdd={(p) => { addUpsell(p); }}
        t={t}
        theme={theme}
      />
      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        items={cart}
        onQty={changeQty}
        onRemove={removeItem}
        onCheckout={checkout}
        onAddMore={() => setCartOpen(false)}
        tableNumber={tableNumber}
        t={t}
        submitting={submitting}
        theme={theme}
      />

      <AlertDialog open={callDialog} onOpenChange={setCallDialog}>
        <AlertDialogContent data-testid="call-waiter-dialog" className="max-w-sm rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl text-[#123026]">{t.srvTitle}</AlertDialogTitle>
            <AlertDialogDescription className="sr-only">{t.srvTitle}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 gap-2.5">
            {[
              ["SERVEUR", t.srvWaiter], ["EAU", t.srvWater], ["PAIN", t.srvBread],
              ["COUVERTS", t.srvCutlery], ["ADDITION", t.srvBill], ["AUTRE", t.srvOther],
            ].map(([type, label]) => (
              <button
                key={type}
                data-testid={`srv-${type.toLowerCase()}`}
                onClick={() => callWaiter(type, label)}
                className="rounded-2xl border border-[#EAE5DC] bg-white hover:bg-[#EBF2EE] text-[#123026] font-semibold text-sm py-4 px-2 transition-colors active:scale-95"
              >
                {label}
              </button>
            ))}
          </div>
          <AlertDialogFooter className="mt-1">
            <AlertDialogCancel data-testid="call-waiter-cancel" className="w-full rounded-full mt-0">{t.cancel}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function NavBtn({ testid, icon: Icon, label, active, badge, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick} className="relative flex flex-col items-center gap-0.5 px-3 py-1.5 min-w-[64px] active:scale-95 transition-transform">
      <div className="relative">
        <Icon className={`w-5 h-5 transition-colors ${active ? "text-[#C69C4A]" : "text-[#5C615E]"}`} strokeWidth={active ? 2.4 : 2} />
        {badge > 0 && (
          <span data-testid={`${testid}-badge`} className="absolute -top-1.5 -right-2 bg-[#C69C4A] text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 grid place-items-center">{badge}</span>
        )}
      </div>
      <span className={`text-[10px] font-semibold ${active ? "text-[#123026]" : "text-[#8A918D]"}`}>{label}</span>
    </button>
  );
}

function CatPill({ active, onClick, label, testid, emoji, icon, all }) {
  return (
    <button
      data-testid={`cat-pill-${testid}`}
      data-active={active ? "true" : "false"}
      onClick={onClick}
      className={`shrink-0 min-h-[44px] rounded-full px-5 py-2.5 text-sm font-bold tracking-wide border transition-all active:scale-95 flex items-center gap-2 ${
        active
          ? "bg-[#123026] text-[#FAF3E0] border-[#123026] shadow-md shadow-[#123026]/20 ring-2 ring-[#C69C4A]/60 ring-offset-1 ring-offset-[#FBF9F5]"
          : all
          ? "bg-[#FAF3E0] text-[#123026] border-[#C69C4A]/50 hover:border-[#C69C4A]"
          : "bg-white text-[#3E443F] border-[#EAE5DC] hover:border-[#C69C4A]/60"
      }`}
    >
      {all ? <Star className="w-4 h-4 text-[#C69C4A]" /> : icon ? icon : emoji ? <span className="text-base leading-none">{emoji}</span> : null}
      {label}
    </button>
  );
}
